import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { hasPermission } from '../utils/permissions';
import { useData } from '../hooks/useData.js';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';
// @ts-ignore
import { StandardPageLayout, LoadingSpinner, Button as CommonButton, Modal, ConfirmModal, FormInput, FormTextarea } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import { apiFetch, getStoredToken } from '../api/index.js';
import { useNotification } from '../context/NotificationContext.jsx';
import { useNavigate } from 'react-router-dom';
import TenantAppRunnerPage from './TenantAppRunnerPage.jsx';
import {
    Dialog, DialogContent, DialogTitle, IconButton,
    ToggleButton, ToggleButtonGroup, Card, CardContent, CardActions, Typography,
    Avatar, Chip, Stack, Box, Grid, Menu, MenuItem, ListItemIcon,
    ListItemText, Divider, TextField, InputAdornment, useTheme, CircularProgress
} from '@mui/material';
import {
    PlusCircle, Package, LayoutDashboard, BrainCircuit, RefreshCw,
    MoreHorizontal, Search, Filter, LayoutGrid, List,
    ArrowRight, Settings, Copy, Share, Trash2, Eye, X, GitBranch, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";


// Resolves the display label for a content item using the contentTypeName from the
// DB-joined content_types table. NO hardcoded name mapping — if you rename a content
// type in content_types, the UI updates automatically.
// Fallback chain: contentTypeName → "Dashboard" (for native dashboard rows) → raw type
const getContentTypeLabel = (item) => {
    if (item.contentTypeName) return item.contentTypeName;
    const rawType = (item.type || item.content_type || '').toLowerCase();
    if (rawType === 'dashboard') return 'Dashboard';
    return rawType ? rawType.charAt(0).toUpperCase() + rawType.slice(1) : '—';
};


const AppBundleForm = ({ bundle, contents, partners, currentUser, onSave, onCancel }) => {
    const { formatDate } = useTimezoneFormatter();
    const isSelected = (assetId, currentAssets) => currentAssets.some(a => a.id === assetId);
    const isPlatformAdmin = hasPermission(currentUser, 'bundles:manage');

    // Initial assets from bundle — normalize type so isSelected() matches availableContents correctly.
    // Flow-published dashboards may be stored in bundle_content_links as asset_type='content' (legacy)
    // but the availableContents list gives them type='dashboard'. We normalize here so the checkboxes
    // show as pre-checked when the form opens for editing.
    const normalizeAssetType = (c) => {
        const rawType = (c.type || '').toLowerCase();
        const rawContentType = (c.content_type || '').toLowerCase();
        const ctName = (c.contentTypeName || '').toLowerCase();
        if (rawType === 'dashboard' || rawContentType === 'dashboard' || ctName === 'dashboard') {
            return 'dashboard';
        }
        return rawType || 'content';
    };

    const initialAssets = bundle?.contents
        ? bundle.contents.map(c => ({ id: c.id, type: normalizeAssetType(c), name: c.name, description: c.description }))
        : (bundle?.content_ids ? bundle.content_ids.map(id => ({ id, type: 'content' })) : []);

    const [formData, setFormData] = useState({
        name: bundle?.name || '',
        description: bundle?.description || '',
        domain: bundle?.domain || '',
        category: bundle?.category || '',
        status: bundle?.status || 'Published',
        partner_ids: bundle?.partner_ids || (bundle?.partner_id ? [bundle.partner_id] : []),
        assets: initialAssets
    });

    // Flow/Dashboard version selections: { [contentId]: { version_number, version_id, version_label } }
    const [flowVersionSelections, setFlowVersionSelections] = useState(() => {
        const init = {};
        initialAssets.forEach(a => {
            if (a.version_content_id) init[a.id] = { version_id: a.version_content_id, version_label: a.version_label || '' };
        });
        return init;
    });
    // Open version pickers per content
    const [openVersionPicker, setOpenVersionPicker] = useState(null);
    const [flowVersions, setFlowVersions] = useState({}); // { [contentId]: versions[] }

    // Group current assets
    const assignedApps = formData.assets.filter(a => a.type === 'dashboard' || a.type === 'content' || a.type === 'ml_model');

    // --- DEPENDENCY RESOLUTION LOGIC ---
    // 1. Identify "Implicitly Linked" Dashboards (Integral to ML Models)
    // 2. Identify "Required" Datasets (from all Dashboards & Models)
    const { dependencies, implicitDashboardIds } = React.useMemo(() => {
        const deps = new Map(); // Use Map to prevent duplicates: id -> {id, name, type}
        const linkedDashIds = new Set();

        // Helper: Add Dataset Dependencies
        const collectDatasets = (contentItem) => {
            if (contentItem?.dataset_ids) {
                contentItem.dataset_ids.forEach(dsId => {
                    const ds = contents?.find(c => c.id === dsId && c.type === 'dataset');
                    if (ds && !deps.has(ds.id)) {
                        deps.set(ds.id, { id: ds.id, name: ds.name, type: 'dataset' });
                    }
                });
            }
        };

        assignedApps.forEach(app => {
            const fullContent = contents?.find(c => c.id === app.id);
            if (!fullContent) return;

            // A. If it's an ML/Content item, check for integral Dashboard
            if (fullContent.dashboard_id) {
                const linkedDash = contents?.find(c => c.id === fullContent.dashboard_id);
                if (linkedDash) {
                    linkedDashIds.add(linkedDash.id);
                    // Add as Dependency (Integral Item)
                    if (!deps.has(linkedDash.id)) {
                        deps.set(linkedDash.id, { id: linkedDash.id, name: linkedDash.name, type: 'dashboard' });
                    }
                    // Also collect datasets from this linked dashboard
                    collectDatasets(linkedDash);
                }
            }

            // B. Collect datasets from the item itself
            collectDatasets(fullContent);
        });

        return {
            dependencies: Array.from(deps.values()),
            implicitDashboardIds: linkedDashIds
        };
    }, [assignedApps, contents]);

    // Available Content for Selection — only Published items
    // EXCLUDE Datasets (Internal only)
    // EXCLUDE Dashboards that are already implicitly linked by a selected ML Model
    const availableContents = [...(contents || [])]
        .filter(c => c.type !== 'dataset')
        .filter(c => !implicitDashboardIds.has(c.id))
        .filter(c => {
            // Only show Published content in bundle assignment — draft items must be published first
            const status = (c.status || '').toLowerCase();
            return status === 'published';
        })
        .sort((a, b) => {
            if (a.type !== b.type) return (a.type || '').localeCompare(b.type || '');
            return (a.name || '').localeCompare(b.name || '');
        });

    const handleChange = (e) => setFormData({ ...formData, [e.target.id]: e.target.value });

    // Unified version loader — supports both flow-backed and dashboard-backed content
    const loadFlowVersions = async (content) => {
        if (flowVersions[content.id]) return; // already loaded
        try {
            const tkn = getStoredToken();
            let endpoint = null;
            if (content.flow_id) {
                endpoint = `/flows/${content.flow_id}/versions`;
            } else if (content.dashboard_id) {
                endpoint = `/dashboards/${content.dashboard_id}/versions`;
            }
            if (!endpoint) return;
            const data = await apiFetch(endpoint, tkn);
            setFlowVersions(prev => ({ ...prev, [content.id]: data || [] }));
        } catch { /* ignore */ }
    };

    const handleContentToggle = (content) => {
        setFormData(prev => {
            const exists = prev.assets.find(a => a.id === content.id);
            let newAssets;
            if (exists) {
                newAssets = prev.assets.filter(a => a.id !== content.id);
            } else {
                // Preserve correct type: 'dashboard' for dashboards, 'content' for everything else.
                // Note: flow-published dashboards come from /business-apps/contents with type='content',
                // but contentTypeName='Dashboard'. We normalise here so the correct asset_type is
                // written to bundle_content_links and downstream my-content routing works correctly.
                const assetType = (content.type === 'dashboard' || (content.contentTypeName || '').toLowerCase() === 'dashboard')
                    ? 'dashboard'
                    : (content.type || 'content');
                newAssets = [...prev.assets, { id: content.id, type: assetType, name: content.name, description: content.description }];
            }
            return { ...prev, assets: newAssets };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Merge explicit assets with implicit dependencies
        // Use a Map keyed on id to deduplicate — same content_id cannot appear twice
        // (can happen when a dependency dashboard was also manually selected by the admin).
        const assetMap = new Map();
        formData.assets.forEach(a => assetMap.set(a.id, a));

        // Attach version selections to flow assets
        assetMap.forEach((a, id) => {
            const sel = flowVersionSelections[id];
            if (sel?.version_id) {
                assetMap.set(id, { ...a, version_content_id: sel.version_id, version_label: sel.version_label });
            }
        });

        // Add implicit dependencies only if not already explicitly selected
        dependencies.forEach(dep => {
            if (!assetMap.has(dep.id)) {
                assetMap.set(dep.id, {
                    id: dep.id,
                    name: dep.name,
                    type: dep.type,
                    content_uid: dep.id
                });
            }
        });

        const finalAssets = Array.from(assetMap.values());
        const payload = { ...formData, assets: finalAssets };
        console.log('[BUNDLE SUBMIT] partner_ids:', payload.partner_ids, 'assets count:', finalAssets.length);
        onSave(payload);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
                <FormInput id="name" label="Bundle Name" value={formData.name} onChange={handleChange} required />
                <div className="grid grid-cols-2 gap-4">
                    <FormInput id="domain" label="Domain (e.g., RCM, Manufacturing)" value={formData.domain} onChange={handleChange} />
                    <FormInput id="category" label="Category (e.g., Dashboards, AI/ML/Flows)" value={formData.category} onChange={handleChange} />
                </div>

                <FormTextarea id="description" label="Description" value={formData.description} onChange={handleChange} rows={2} />


                {/* Partner Scoping — visible only to Platform Admins (r1, r2) */}
                {isPlatformAdmin && (
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium leading-none text-gray-700 dark:text-gray-300">
                            Partner Scope
                            <span className="ml-1 text-xs text-gray-400 font-normal">(leave blank = platform-wide, visible to all partners)</span>
                        </label>

                        {/* Selected partner chips */}
                        {formData.partner_ids.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {formData.partner_ids.map(pid => {
                                    const p = (partners || []).find(x => x.id === pid);
                                    return (
                                        <span key={pid}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700">
                                            {p?.name || pid}
                                            <button type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, partner_ids: prev.partner_ids.filter(id => id !== pid) }))}
                                                className="hover:text-indigo-900 dark:hover:text-indigo-100 ml-0.5">
                                                &times;
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}

                        {/* Multi-checkbox partner list */}
                        <div className="max-h-36 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-slate-950 divide-y divide-gray-100 dark:divide-gray-800">
                            <label className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 text-sm text-gray-500 dark:text-gray-400 italic">
                                <input type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300"
                                    checked={formData.partner_ids.length === 0}
                                    onChange={() => setFormData(prev => ({ ...prev, partner_ids: [] }))}
                                />
                                -- Platform-wide (visible to all partners) --
                            </label>
                            {(partners || []).map(p => (
                                <label key={p.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 text-sm text-gray-800 dark:text-gray-200">
                                    <input type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        checked={formData.partner_ids.includes(p.id)}
                                        onChange={() => setFormData(prev => {
                                            const already = prev.partner_ids.includes(p.id);
                                            return {
                                                ...prev,
                                                partner_ids: already
                                                    ? prev.partner_ids.filter(id => id !== p.id)
                                                    : [...prev.partner_ids, p.id]
                                            };
                                        })}
                                    />
                                    {p.name}
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {/* Status — visible only to Platform Admins */}
                {isPlatformAdmin && (
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="status" className="text-sm font-medium leading-none text-gray-700 dark:text-gray-300">Status</label>
                        <select
                            id="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-950 dark:border-gray-800 dark:bg-slate-950 dark:focus:ring-slate-300"
                        >
                            <option value="Published">Published</option>
                            <option value="Draft">Draft</option>
                        </select>
                    </div>
                )}
            </div>

            <div className="border-t dark:border-gray-700 pt-4">
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>Assigned Apps (Dashboards & Models)</Typography>
                <div className="space-y-2 max-h-60 overflow-y-auto border dark:border-gray-600 p-2 rounded-md bg-gray-50 dark:bg-gray-800/50">
                    {availableContents?.map(content => (
                        <div key={content.id} className="border-b border-gray-200 dark:border-gray-700 last:border-0">
                            <label className="flex items-center space-x-3 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md cursor-pointer transition-colors">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    checked={isSelected(content.id, formData.assets)}
                                    onChange={() => handleContentToggle(content)}
                                />
                                <div className="flex items-center gap-3 flex-1">
                                    <Avatar variant="rounded" sx={{ width: 28, height: 28, bgcolor: (content.type === 'dashboard' || content.content_type === 'dashboard') ? 'secondary.light' : 'info.light' }}>
                                        <DynamicIcon name={(content.type === 'dashboard' || content.content_type === 'dashboard') ? 'LayoutDashboard' : content.flow_id ? 'GitBranch' : 'BrainCircuit'} className="w-4 h-4 text-gray-700" />
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{content.name}</span>
                                            {/* Version badge for flow or dashboard apps */}
                                            {(content.flow_id || content.dashboard_id) && (
                                                <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-300">
                                                    {flowVersionSelections[content.id]?.version_label || content.version || 'Latest'}
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-xs text-gray-500 capitalize">{content.flow_id ? 'Flow App' : content.dashboard_id ? 'Dashboard App' : content.type?.replace('_', ' ')}</span>
                                    </div>
                                    {/* Version picker — for both flow and dashboard items */}
                                    {(content.flow_id || content.dashboard_id) && (
                                        <div className="flex-shrink-0 relative" onClick={e => e.stopPropagation()}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    loadFlowVersions(content);
                                                    setOpenVersionPicker(openVersionPicker === content.id ? null : content.id);
                                                }}
                                                className="flex items-center gap-1 px-2 py-0.5 rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-500 dark:text-slate-400 text-xs hover:border-blue-200 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                            >
                                                <GitBranch className="w-3 h-3" />
                                                <span>{flowVersionSelections[content.id]?.version_label || 'Latest'}</span>
                                                <ChevronDown className="w-3 h-3" />
                                            </button>
                                            {openVersionPicker === content.id && (
                                                <div className="absolute z-20 mt-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl min-w-[240px] max-w-[300px] right-0">
                                                    <div className="p-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setFlowVersionSelections(prev => { const n = {...prev}; delete n[content.id]; return n; });
                                                                setOpenVersionPicker(null);
                                                            }}
                                                            className="block w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-700 dark:text-slate-200 font-medium"
                                                        >
                                                            Latest (auto)
                                                        </button>
                                                        {(flowVersions[content.id] || []).map(v => (
                                                            <button
                                                                key={v.id}
                                                                type="button"
                                                                onClick={() => {
                                                                    setFlowVersionSelections(prev => ({ ...prev, [content.id]: { version_id: v.id, version_label: v.version || `v${v.version_number}` } }));
                                                                    setOpenVersionPicker(null);
                                                                }}
                                                                className={`block w-full text-left px-2 py-2 text-xs rounded hover:bg-blue-50 dark:hover:bg-slate-800 ${
                                                                    flowVersionSelections[content.id]?.version_id === v.id
                                                                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                                                                        : 'text-gray-700 dark:text-slate-200'
                                                                }`}
                                                            >
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="font-semibold">{v.version || `v${v.version_number}`}</span>
                                                                    {v.is_active && <span className="text-[9px] px-1 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 font-bold">● Live</span>}
                                                                </div>
                                                                {v.description && (
                                                                    <span className="block text-[10px] text-gray-500 dark:text-slate-400 mt-0.5 line-clamp-2">{v.description}</span>
                                                                )}
                                                                {v.published_at && <span className="block text-[10px] text-gray-400 mt-0.5">{new Date(v.published_at).toLocaleDateString()}</span>}
                                                            </button>
                                                        ))}
                                                        {!flowVersions[content.id] && (
                                                            <div className="px-2 py-1.5 text-xs text-gray-400">Loading versions…</div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </label>
                        </div>
                    ))}
                    {(!availableContents || availableContents.length === 0) && <p className="text-sm text-gray-500 p-2">No Published Contents available.</p>}
                </div>
            </div>

            {dependencies.length > 0 && (
                <div className="border-t dark:border-gray-700 pt-4">
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DynamicIcon name="Layers" className="w-4 h-4" />
                        Required Dependency Items
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                        The following items are integral to the selected apps (Linked Dashboards, Required Datasets). They will be automatically included.
                    </Typography>

                    <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-2 space-y-1">
                        {dependencies.map(dep => (
                            <div key={dep.id} className="flex items-center gap-2 p-1.5 px-3 bg-white dark:bg-gray-700 rounded border border-gray-100 dark:border-gray-600">
                                <DynamicIcon
                                    name={dep.type === 'dashboard' ? 'LayoutDashboard' : 'Database'}
                                    className={`w-3 h-3 ${dep.type === 'dashboard' ? 'text-indigo-400' : 'text-gray-400'}`}
                                />
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{dep.name}</span>
                                <span className="text-[10px] text-gray-400 ml-auto uppercase tracking-wider">{dep.type}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
                <Button variant="secondary" type="button" onClick={onCancel} withShadow>Cancel</Button>
                <Button type="submit">{bundle ? 'Save Changes' : 'Create Bundle'}</Button>
            </div>
        </form>
    );
};


const BusinessAppsPage = ({ token, currentUser, onSelectAsset }) => {
    const { addNotification } = useNotification();
    const theme = useTheme();

    // Data Fetching
    // publishedContent = only Published (for the "Contents" tab display)
    // allContent = all statuses (for the bundle form, so draft bundles can include draft flow apps)
    const { data: publishedContent, isLoading: loadingContent } = useData('/business-apps/contents', token);
    const { data: allContent } = useData('/business-apps/contents?all=true', token);
    const { data: appBundles, isLoading: loadingBundles, refresh: refreshBundles } = useData('/business-apps/bundles', token);
    // Partners list — only fetched for Platform Admins to populate partner scope dropdown
    const isPlatformAdmin = hasPermission(currentUser, 'bundles:manage');
    const { data: partners } = useData(isPlatformAdmin ? '/partners' : null, token);

    // State
    const [viewMode, setViewMode] = useState('bundles'); // 'bundles' | 'contents'
    const [viewLayout, _setViewLayout] = useState(() => {
        return localStorage.getItem('business-apps-view-mode') || 'grid';
    });
    const setViewLayout = (mode) => {
        localStorage.setItem('business-apps-view-mode', mode);
        _setViewLayout(mode);
    }; // 'grid' | 'list'
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBundle, setEditingBundle] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [previewAsset, setPreviewAsset] = useState(null);
    // Cache of per-content version lists { [contentId]: version[] }
    const [contentVersionsCache, setContentVersionsCache] = useState({});

    // Batch-fetch all versions when the Content tab is shown
    React.useEffect(() => {
        if (viewMode !== 'contents') return;
        const items = publishedContent || [];
        const unfetched = items.filter(c => contentVersionsCache[c.id] === undefined && c.type !== 'dataset');
        if (unfetched.length === 0) return;

        // Mark them all as loading in one state update
        setContentVersionsCache(prev => {
            const next = { ...prev };
            unfetched.forEach(c => { next[c.id] = null; });
            return next;
        });

        // Fetch in parallel
        unfetched.forEach(async (item) => {
            try {
                let endpoint = null;
                if (item.flow_id)            endpoint = `/flows/${item.flow_id}/versions`;
                else if (item.dashboard_id)  endpoint = `/dashboards/${item.dashboard_id}/versions`;
                else if (item.type === 'dashboard') endpoint = `/dashboards/${item.id}/versions`;
                const data = endpoint ? await apiFetch(endpoint, token) : [];
                setContentVersionsCache(prev => ({ ...prev, [item.id]: Array.isArray(data) ? data : [] }));
            } catch {
                setContentVersionsCache(prev => ({ ...prev, [item.id]: [] }));
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, publishedContent]);

    // Menu States
    const [cardMenuAnchor, setCardMenuAnchor] = useState(null);
    const [menuTargetItem, setMenuTargetItem] = useState(null);

    // Filter Logic
    const filteredBundles = (appBundles || []).filter(b =>
        b.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredContent = (publishedContent || []).filter(c =>
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Derived State for Preview
    const virtualInstance = React.useMemo(() => {
        if (!previewAsset) return null;
        // If it's a bundle, return it as is (it has contents)
        if (previewAsset.type === 'bundle' || previewAsset.contents) return previewAsset;

        // If it's a single item (dashboard/model), wrap it to look like a bundle instance for PreviewLayout
        return {
            id: 'virtual-wrapper',
            name: previewAsset.name,
            contents: [previewAsset]
        };
    }, [previewAsset]);

    // Handlers
    const handleAddBundle = () => {
        setEditingBundle(null);
        setIsModalOpen(true);
    };

    const handleEditBundle = async (bundle) => {
        try {
            const fullBundle = await apiFetch(`/business-apps/bundles/${bundle.id}`, token);
            setEditingBundle(fullBundle);
            setIsModalOpen(true);
        } catch (err) {
            addNotification(`Error loading bundle: ${err.message}`, 'error');
        }
    };

    const handleDeleteBundle = (bundle) => {
        setConfirmDelete(bundle);
    };

    const handlePreview = async (asset) => {
        if (!asset) return;

        // If it's a bundle, we need to fetch the full details (contents) because the list view only has metadata
        if (asset.type === 'bundle' || !asset.type) { // Default to bundle if type missing in list
            try {
                const fullBundle = await apiFetch(`/business-apps/bundles/${asset.id}`, token);
                // Ensure contents are populated or default to empty
                if (!fullBundle.contents) fullBundle.contents = [];

                // Inject type if missing (for the Runner)
                fullBundle.type = 'bundle';

                setPreviewAsset(fullBundle);
            } catch (e) {
                console.error("Failed to fetch bundle details for preview", e);
                // Fallback to the partial object, better than nothing
                setPreviewAsset({ ...asset, type: 'bundle' });
            }
        } else {
            // Dashboards/Models are likely fully populated or don't need deep fetch
            setPreviewAsset(asset);
        }
    };

    const confirmDeletion = async () => {
        if (!confirmDelete) return;
        try {
            await apiFetch(`/business-apps/bundles/${confirmDelete.id}`, token, { method: 'DELETE' });
            addNotification(`Bundle "${confirmDelete.name}" deleted successfully.`);
            refreshBundles();
        } catch (err) {
            addNotification(`Error deleting bundle: ${err.message}`, 'error');
        } finally {
            setConfirmDelete(null);
        }
    };

    const handleSaveBundle = async (bundleData) => {
        const method = editingBundle ? 'PUT' : 'POST';
        const endpoint = editingBundle ? `/business-apps/bundles/${editingBundle.id}` : '/business-apps/bundles';
        try {
            await apiFetch(endpoint, token, { method, body: JSON.stringify(bundleData) });
            addNotification(`Bundle "${bundleData.name}" ${editingBundle ? 'updated' : 'created'} successfully.`);
            refreshBundles();
            setIsModalOpen(false);
        } catch (err) {
            addNotification(`Error saving bundle: ${err.message}`, 'error');
        }
    };

    const handleAction = (action, item) => {
        if (action === 'edit') handleEditBundle(item);
        if (action === 'delete') handleDeleteBundle(item);
        if (action === 'preview') handlePreview(item);
        if (action === 'clone') handleCloneBundle(item);
        if (action === 'export') handleExportBundle(item);
        if (action === 'export-content') handleExportContent(item);
        if (action === 'view') onSelectAsset({ id: item.id, type: 'content' });
    };

    const handleExportContent = async (content) => {
        try {
            let endpoint = '';
            if (content.type === 'dashboard') endpoint = `/dashboards/${content.id}/export`;
            else if (content.type === 'dataset') endpoint = `/datasets/${content.id}/export`;
            else {
                addNotification("Export not supported for this content type.", "warning");
                return;
            }
            const data = await apiFetch(endpoint, token, { method: 'POST' });
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${content.type}-${content.name.replace(/\s+/g, '_')}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            addNotification(`Export failed: ${err.message}`, 'error');
        }
    };

    const handleCloneBundle = async (bundle) => {
        try {
            await apiFetch(`/business-apps/bundles/${bundle.id}/clone`, token, { method: 'POST' });
            addNotification("Bundle cloned successfully", "success");
            refreshBundles();
        } catch (err) {
            addNotification(`Clone failed: ${err.message}`, 'error');
        }
    };

    const handleExportBundle = async (bundle) => {
        try {
            const data = await apiFetch(`/business-apps/bundles/${bundle.id}/export`, token, { method: 'POST' });
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `bundle-${bundle.name.replace(/\s+/g, '_')}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            addNotification(`Export started for "${bundle.name}"`);
        } catch (err) {
            addNotification(`Export failed: ${err.message}`, 'error');
        }
    };

    // Header Components
    const headerActions = (
        <div className="flex gap-2">
            {viewMode === 'bundles' && (
                <CommonButton onClick={handleAddBundle} sx={{ height: 30 }}>
                    <PlusCircle className="mr-2 h-4 w-4" /> CREATE BUNDLE
                </CommonButton>
            )}
        </div>
    );

    const filterControls = (
        <div className="flex items-center gap-4">
            <div className="relative max-w-md group w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-[#002F5D] dark:text-slate-500 transition-colors" />
                <input
                    type="text"
                    placeholder={`Search ${viewMode === 'bundles' ? 'Bundles' : 'Content'}...`}
                    className="w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 py-2 pl-10 pr-4 rounded-xl shadow-sm text-sm focus:ring-2 focus:ring-blue-100 dark:text-slate-100 transition-shadow outline-none placeholder:text-gray-400 dark:placeholder:text-slate-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="flex items-center bg-slate-100 dark:bg-slate-900 rounded-lg p-1 border border-gray-200 dark:border-slate-800">
                <Button
                    variant="ghost"
                    size="sm"
                    className={`px-3 h-8 text-xs font-medium transition-all duration-200 ${viewMode === 'bundles' ? 'bg-white text-blue-700 shadow-sm dark:bg-slate-800 dark:text-blue-400 font-semibold' : 'text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200'}`}
                    onClick={() => setViewMode('bundles')}
                >
                    <Package className="w-4 h-4 mr-2" /> Bundles
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    className={`px-3 h-8 text-xs font-medium transition-all duration-200 ${viewMode === 'contents' ? 'bg-white text-blue-700 shadow-sm dark:bg-slate-800 dark:text-blue-400 font-semibold' : 'text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200'}`}
                    onClick={() => setViewMode('contents')}
                >
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Content
                </Button>
            </div>

            <div className="flex items-center bg-slate-100 dark:bg-slate-900 rounded-lg p-1 border border-gray-200 dark:border-slate-800">
                <Button
                    variant="ghost"
                    size="sm"
                    className={`px-2 h-8 transition-all duration-200 ${viewLayout === 'grid' ? 'bg-white text-blue-700 shadow-sm dark:bg-slate-800 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200'}`}
                    onClick={() => setViewLayout('grid')}
                >
                    <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    className={`px-2 h-8 transition-all duration-200 ${viewLayout === 'list' ? 'bg-white text-blue-700 shadow-sm dark:bg-slate-800 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200'}`}
                    onClick={() => setViewLayout('list')}
                >
                    <List className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );

    // Standard Card Rendering
    const renderCard = (item, isBundle) => (
        <div
            key={item.id}
            onClick={() => handlePreview(item)}
            className="group bg-white dark:bg-slate-900 rounded-2xl p-5 border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer relative overflow-hidden flex flex-col h-full"
        >
            {/* Decorative grad */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#002F5D] to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />

            <div className="flex justify-between items-start mb-4">
                <div className={`flex items-center justify-center p-2.5 rounded-xl group-hover:text-white transition-colors duration-300 ${isBundle ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 group-hover:bg-blue-600' : 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 group-hover:bg-indigo-600'}`}>
                    {isBundle ? <Package className="h-6 w-6" /> : (item.type === 'dashboard' ? <LayoutDashboard className="h-6 w-6" /> : <BrainCircuit className="h-6 w-6" />)}
                </div>
                <div className="flex gap-1">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                                <MoreHorizontal className="w-4 h-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40 bg-white dark:bg-slate-900 shadow-xl border-gray-100 dark:border-slate-800">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handlePreview(item); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                <Eye className="w-4 h-4 mr-2" /> Preview
                            </DropdownMenuItem>
                            {isBundle ? (
                                <>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEditBundle(item); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                        <Settings className="w-4 h-4 mr-2" /> Manage
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleCloneBundle(item); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                        <Copy className="w-4 h-4 mr-2" /> Clone
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleExportBundle(item); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                        <Share className="w-4 h-4 mr-2" /> Export
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDeleteBundle(item); }} className="text-red-600 dark:text-red-400 cursor-pointer focus:text-red-600 dark:focus:text-red-400 focus:bg-red-50 dark:focus:bg-red-900/20">
                                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                                    </DropdownMenuItem>
                                </>
                            ) : (
                                <>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onSelectAsset({ id: item.id, type: 'content' }); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                        <Eye className="w-4 h-4 mr-2" /> View Details
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleExportContent(item); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                        <Share className="w-4 h-4 mr-2" /> Export Content
                                    </DropdownMenuItem>
                                </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            <div className="mb-4 flex-grow">
                <h3 className="font-bold text-gray-900 dark:text-slate-100 mb-1 group-hover:text-[#002F5D] dark:group-hover:text-blue-400 transition-colors truncate" title={item.name}>{item.name}</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">v{(item.version || '1.0').replace(/^v/i, '')}</p>
                <div className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mt-2 min-h-[2.5em]">
                    {item.description || 'No description provided'}
                </div>
            </div>

            {isBundle && (
                <div className="pt-4 border-t border-gray-50 dark:border-slate-800">
                    <div className="flex flex-wrap gap-2 text-[10px] text-slate-500 dark:text-slate-400">
                        <span className="bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">
                            {item.dashboard_count ?? item.contents?.filter(c => c.type === 'dashboard').length ?? 0} Dashboards
                        </span>
                        <span className="bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">
                            {item.content_count || 0} Models
                        </span>
                        <span className="bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-500 px-2 py-0.5 rounded-md ml-auto">
                            {item.tenant_count || 0} Tenants
                        </span>
                    </div>
                    {/* Partner scope badges */}
                    {isPlatformAdmin && (
                        <div className="mt-2 flex flex-wrap gap-1">
                            {(item.partner_ids && item.partner_ids.length > 0) ? (
                                item.partner_ids.map(pid => {
                                    const p = (partners || []).find(x => x.id === pid);
                                    return (
                                        <span key={pid} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700">
                                            {p?.name || pid}
                                        </span>
                                    );
                                })
                            ) : (
                                <span className="text-[10px] text-slate-400 dark:text-slate-500 italic">Platform-wide</span>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    // List View Rendering
    const renderListView = (items, isBundle) => (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
            <Table>
                <TableHeader className="bg-gray-50/50 dark:bg-slate-900/50">
                    <TableRow className="border-b border-gray-200 dark:border-slate-800">
                        <TableHead className="w-[40%] text-gray-500 dark:text-slate-400">Name</TableHead>
                        <TableHead className="text-gray-500 dark:text-slate-400">Contents</TableHead>
                        {!isBundle && <TableHead className="text-gray-500 dark:text-slate-400">Versions</TableHead>}
                        <TableHead className="text-gray-500 dark:text-slate-400">Description</TableHead>
                        <TableHead className="text-right text-gray-500 dark:text-slate-400">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {items.map((item) => (
                        <TableRow
                            key={item.id}
                            className="cursor-pointer hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-gray-100 dark:border-slate-800"
                            onClick={() => handlePreview(item)}
                        >
                            <TableCell>
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded text-white ${isBundle ? 'bg-blue-500' : 'bg-indigo-500'}`}>
                                        {isBundle ? <Package className="w-4 h-4" /> : <LayoutDashboard className="w-4 h-4" />}
                                    </div>
                                    <div>
                                        <div className="font-medium text-gray-900 dark:text-slate-100">{item.name}</div>
                                        {item.contentTypeName && (
                                            <div className="text-[10px] text-gray-400 dark:text-slate-500 capitalize">{item.contentTypeName}</div>
                                        )}
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                {isBundle ? (
                                    <div className="flex gap-2">
                                        <Chip size="small" label={`${item.dashboard_count || 0} Dashboards`} />
                                        <Chip size="small" label={`${item.content_count || 0} Flows`} />
                                        <Chip size="small" label={`${item.tenant_count || 0} Tenants`} color="warning" variant="outlined" />
                                    </div>
                                ) : (
                                    <Chip label={item.type} size="small" variant="outlined" />
                                )}
                            </TableCell>
                            {/* Versions column — shows ONLY the active (assigned) version */}
                            {!isBundle && (() => {
                                const vers = contentVersionsCache[item.id];
                                // Resolve active version from full version history
                                if (Array.isArray(vers) && vers.length > 0) {
                                    const sorted = [...vers].sort((a, b) => a.version_number - b.version_number);
                                    const maxVn = Math.max(...sorted.map(v => v.version_number));
                                    const activeVer = sorted.find(v => v.is_active)
                                        || sorted.find(v => v.version_number === maxVn);
                                    return (
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            {activeVer ? (
                                                <span
                                                    title={activeVer.description
                                                        ? `${activeVer.version || `v${activeVer.version_number}`}: ${activeVer.description}`
                                                        : `Active version: ${activeVer.version || `v${activeVer.version_number}`}`}
                                                    className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border leading-none whitespace-nowrap bg-emerald-100 border-emerald-400 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-600 dark:text-emerald-300"
                                                >
                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 animate-pulse"></span>
                                                    {activeVer.version || `v${activeVer.version_number}`}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-gray-400">—</span>
                                            )}
                                        </TableCell>
                                    );
                                }
                                // Immediate fallback: use item.version from the list API response, or default to v1 for dashboards
                                if (item.version || item.type === 'dashboard') {
                                    const displayVer = item.version || '1.0';
                                    const formattedVer = displayVer.toString().startsWith('v') ? displayVer : `v${displayVer.toString().replace(/^v/i, '')}`;
                                    
                                    return (
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border leading-none bg-emerald-100 border-emerald-400 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-600 dark:text-emerald-300">
                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 animate-pulse"></span>
                                                {formattedVer}
                                            </span>
                                        </TableCell>
                                    );
                                }
                                return (
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        {vers === null
                                            ? <span className="text-xs text-gray-400 animate-pulse">loading…</span>
                                            : <span className="text-xs text-gray-400">—</span>}
                                    </TableCell>
                                );
                            })()}
                            <TableCell className="max-w-xs truncate text-gray-500 dark:text-slate-400">
                                {item.description || "No description"}
                            </TableCell>
                            <TableCell className="text-right">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                                            <MoreHorizontal className="w-4 h-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-40 bg-white dark:bg-slate-900 shadow-xl border-gray-100 dark:border-slate-800">
                                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handlePreview(item); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                            <Eye className="w-4 h-4 mr-2" /> Preview
                                        </DropdownMenuItem>
                                        {isBundle ? (
                                            <>
                                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEditBundle(item); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                    <Settings className="w-4 h-4 mr-2" /> Manage
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleCloneBundle(item); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                    <Copy className="w-4 h-4 mr-2" /> Clone
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleExportBundle(item); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                    <Share className="w-4 h-4 mr-2" /> Export
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDeleteBundle(item); }} className="text-red-600 dark:text-red-400 cursor-pointer focus:text-red-600 dark:focus:text-red-400 focus:bg-red-50 dark:focus:bg-red-900/20">
                                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                                </DropdownMenuItem>
                                            </>
                                        ) : (
                                            <>
                                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onSelectAsset({ id: item.id, type: 'content' }); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                    <Eye className="w-4 h-4 mr-2" /> View Details
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleExportContent(item); }} className="cursor-pointer dark:text-slate-200 dark:hover:bg-slate-800">
                                                    <Share className="w-4 h-4 mr-2" /> Export Content
                                                </DropdownMenuItem>
                                            </>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );

    if (loadingBundles || loadingContent) return <LoadingSpinner />;

    const itemsToRender = viewMode === 'bundles' ? filteredBundles : filteredContent;
    const isBundleView = viewMode === 'bundles';

    return (
        <StandardPageLayout
            title={viewMode === 'bundles' ? "Business Apps" : "Published Contents"}
            description={viewMode === 'bundles' ? "Manage and publish application bundles." : "Library of published dashboards and models."}
            headerActions={headerActions}
            filterControls={filterControls}
        >
            <div className="pb-10 min-h-[50vh]">
                {itemsToRender.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="bg-slate-50 dark:bg-slate-900/50 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                            <Package className="h-10 w-10 text-slate-300 dark:text-slate-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                            No items found
                        </h3>
                        <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-sm mx-auto">
                            {searchTerm
                                ? 'Try adjusting your search query'
                                : 'No items match your criteria.'}
                        </p>
                    </div>
                ) : (
                    viewLayout === 'list' ? (
                        renderListView(itemsToRender, isBundleView)
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {itemsToRender.map(item => renderCard(item, isBundleView))}
                        </div>
                    )
                )}
            </div>

            {/* Modals */}
            <Dialog
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{editingBundle ? 'Edit App Bundle' : 'Create New Bundle'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <AppBundleForm
                            bundle={editingBundle}
                            contents={allContent || publishedContent || []}
                            partners={partners || []}
                            currentUser={currentUser}
                            onSave={handleSaveBundle}
                            onCancel={() => setIsModalOpen(false)}
                        />
                    </Box>
                </DialogContent>
            </Dialog>

            <ConfirmModal
                isOpen={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                onConfirm={confirmDeletion}
                title={`Delete ${confirmDelete?.name}?`}
            >
                Are you sure you want to delete this bundle? This action acts on the bundle definition only.
            </ConfirmModal>

            {/* Preview Modal */}
            <Dialog
                open={!!previewAsset}
                onClose={() => setPreviewAsset(null)}
                fullScreen
                PaperProps={{
                    sx: {
                        top: 0,
                        m: 0,
                        maxWidth: '100vw',
                        height: '100vh',
                        bgcolor: 'background.default'
                    }
                }}
            >
                <DialogTitle sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                    <div className="flex items-center gap-2">
                        <DynamicIcon name="Eye" className="w-5 h-5" />
                        <span>Previewing: {previewAsset?.name}</span>
                        <Chip label="Library Preview" size="small" />
                    </div>
                    <IconButton onClick={() => setPreviewAsset(null)}>
                        <DynamicIcon name="X" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, m: 0, overflow: 'hidden' }}>
                    {virtualInstance && (
                        <PreviewLayout virtualInstance={virtualInstance} initialType={previewAsset?.type} />
                    )}
                </DialogContent>
            </Dialog>
        </StandardPageLayout>
    );
};

// Read-only bundle preview — no execution UI
const PreviewLayout = ({ virtualInstance }) => {
    const contents       = virtualInstance.contents || [];
    const isDash = (c) => c.type === 'dashboard' || c.content_type === 'dashboard';
    const dashboardItems = contents.filter(isDash);
    const flowItems      = contents.filter(c => !isDash(c) && c.type !== 'dataset');
    const datasetItems   = contents.filter(c => c.type === 'dataset');
    const totalItems     = contents.length;

    const [selectedId, setSelectedId] = React.useState(null);

    const typeColor = (c) => isDash(c) ? 'bg-blue-500' : c.type === 'dataset' ? 'bg-emerald-500' : 'bg-indigo-500';
    const typeBadge = (c) => isDash(c) ? 'Dashboard' : c.type === 'dataset' ? 'Dataset' : 'Flow / App';
    const typeIcon  = (c) => isDash(c)
        ? <LayoutDashboard className="w-4 h-4" />
        : <DynamicIcon name="Workflow" className="w-4 h-4" />;

    const renderSection = (label, items, iconName) => {
        if (!items || items.length === 0) return null;
        return (
            <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                    <DynamicIcon name={iconName} className="w-4 h-4 text-gray-400" />
                    <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400">{label}</h4>
                    <span className="ml-auto text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-500 px-2 py-0.5 rounded-full">{items.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map(item => {
                        const isSel = selectedId === item.id;
                        return (
                            <div
                                key={item.id}
                                onClick={() => setSelectedId(isSel ? null : item.id)}
                                className={`rounded-2xl border p-4 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg ${
                                    isSel ? 'border-indigo-300 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-900/20 shadow-md'
                                          : 'border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-gray-200'}`}
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div className={`w-9 h-9 rounded-xl ${typeColor(item)} text-white flex items-center justify-center shadow-sm`}>
                                        {typeIcon(item)}
                                    </div>
                                    <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full bg-gray-50 dark:bg-gray-800 text-gray-400">{typeBadge(item)}</span>
                                </div>
                                <h3 className="font-bold text-sm text-gray-900 dark:text-gray-100 mb-1 truncate" title={item.name}>{item.name}</h3>
                                <p className="text-xs text-gray-400 line-clamp-2 min-h-[2.5em]">
                                    {item.description || (isDash(item) ? 'View data insights' : 'Automated workflow')}
                                </p>
                                {item.version && (
                                    <div className="mt-3 pt-3 border-t border-gray-50 dark:border-gray-800">
                                        <span className="text-[10px] text-gray-400 font-mono">v{(item.version || '').replace(/^v/i, '')}</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="flex bg-gray-50 dark:bg-gray-900 -m-6" style={{ height: 'calc(100vh - 60px)' }}>
            {/* Sidebar */}
            <div className="w-56 flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto flex flex-col">
                <div className="p-4 border-b border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-2 mb-1">
                        <div className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center shrink-0">
                            <Package className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-sm text-gray-800 dark:text-gray-100 truncate">{virtualInstance.name}</span>
                    </div>
                    {virtualInstance.description && <p className="text-[11px] text-gray-400 mt-1 line-clamp-2">{virtualInstance.description}</p>}
                </div>
                <div className="p-3 space-y-0.5 flex-1">
                    {dashboardItems.length > 0 && (
                        <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-gray-600 dark:text-gray-300">
                            <LayoutDashboard className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                            <span>{dashboardItems.length} Dashboard{dashboardItems.length !== 1 ? 's' : ''}</span>
                        </div>
                    )}
                    {flowItems.length > 0 && (
                        <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-gray-600 dark:text-gray-300">
                            <DynamicIcon name="Workflow" className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                            <span>{flowItems.length} Flow{flowItems.length !== 1 ? 's' : ''}</span>
                        </div>
                    )}
                    {datasetItems.length > 0 && (
                        <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-gray-600 dark:text-gray-300">
                            <DynamicIcon name="Database" className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                            <span>{datasetItems.length} Dataset{datasetItems.length !== 1 ? 's' : ''}</span>
                        </div>
                    )}
                </div>
                <div className="p-3 border-t border-gray-100 dark:border-gray-800">
                    <span className="text-[10px] text-gray-400">{totalItems} item{totalItems !== 1 ? 's' : ''} in bundle</span>
                </div>
            </div>

            {/* Read-only card gallery */}
            <div className="flex-1 overflow-y-auto px-8 py-6">
                <div className="mb-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{virtualInstance.name}</h2>
                    {virtualInstance.description && <p className="text-sm text-gray-500 mt-1">{virtualInstance.description}</p>}
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-xs bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 px-2.5 py-1 rounded-full font-medium">{totalItems} item{totalItems !== 1 ? 's' : ''}</span>
                        {virtualInstance.domain && <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 px-2.5 py-1 rounded-full">{virtualInstance.domain}</span>}
                        {virtualInstance.category && <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 px-2.5 py-1 rounded-full">{virtualInstance.category}</span>}
                    </div>
                </div>
                {totalItems === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mb-4">
                            <Package className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                        </div>
                        <h3 className="font-semibold text-gray-500 mb-1">No contents in this bundle</h3>
                        <p className="text-sm text-gray-400">Add dashboards or flows to see them here.</p>
                    </div>
                ) : (
                    <>
                        {renderSection('Dashboards', dashboardItems, 'LayoutDashboard')}
                        {renderSection('Flows & Applications', flowItems, 'Workflow')}
                        {renderSection('Datasets', datasetItems, 'Database')}
                    </>
                )}
            </div>
        </div>
    );
};

export default BusinessAppsPage;